import { FC, useCallback, useMemo, useState } from "react";
import { Title, Wrapper } from "./styles";
import { Layout } from "./components/Layout";
import { getComputeAppName, Platforms } from "./section/Platforms";
import { Categories } from "./section/Categories";
import { API, Lang, Tag, TagsAPI } from "@brg/api";
import { TagAdminActions, TagAdminSelector } from "shared/reducers/tagAdminSlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategoryEdit, fetchCurrentPlatformData, updateEditedCategory } from "shared/actions/tagAdminActions";
import { useTranslation } from "react-i18next";
import DroppableCategoryList from "shared/modals/selectTagsModal/components/DroppableCategory/DroppableCategoryList";
import { CategoryEdit, CurrentLangT } from "./section/CategoryEdit";
import { useModal } from "core";
import { useToasts } from "@brg/ui";
import { LangObjectArray } from "@brg/api/dist/api-core/utils/lang-utils";
import { ADMIN_TAGS_TYPE } from "./types";

export const getLangTitle = (arr: LangObjectArray, lang: string) => {
    return arr?.find((i) => i?.lang.toLowerCase() === lang)?.name
}

export const createDefaultLangWithLocal = () => {
    return [{ name: '', lang: 'ru', currentLang: 'ru'}, { name: '', lang: 'en', currentLang: 'ru'}]
}

export enum TAG_ADMIN_OPERATION {
    remove = 'remove',
    add = 'add'
}

enum TAG_LEVEL {
    common = 0,
    subcategory = 1
}

export const MainTagAdminPage: FC = () => {
    const dispatch = useDispatch();
    const modal = useModal();
    const toast = useToasts();
    const {t, i18n} = useTranslation();
    const [time, setTime] = useState(Date.now());

    const { 
        currentPlatform,
        type
    }  = useSelector(TagAdminSelector);

    const computeCategories = useMemo(
        () => currentPlatform?.categories?.length ? API.toLang(currentPlatform?.categories, i18n.language) : [] 
    , [currentPlatform, i18n.language, time]) as Tag<'Local'>[];

    const contextServices = useMemo(() => [{ 
        service: currentPlatform?.platform?.appName ?? '', 
        context: currentPlatform?.platform?.context ?? '' 
    }], [currentPlatform])

    const onCategoryEdit = useCallback((id: string, isChild?: boolean ) => {
        dispatch(fetchCategoryEdit({ id, isChild }))
        dispatch(TagAdminActions.toggleEditMode(true))
        setTime(Date.now());
    } , [dispatch])
    
    const onTagEdit = useCallback((tagData: any) => {
        modal.show('createTag', {
            isModal: true,
            isEducation: type === ADMIN_TAGS_TYPE.education,
            editTag: tagData,
            onUpdateTag: (updatedTag: any) => {
                dispatch(fetchCurrentPlatformData({ 
                    platformName: getComputeAppName(currentPlatform?.platform),
                }))
                if (currentPlatform?.editCategory?.mainTag?.tagId) {
                    onCategoryEdit(currentPlatform.editCategory.mainTag.tagId);
                }
                toast.info(t('tags_modal.update_tag_success'));
                setTime(Date.now());
            },
            onClose: () => modal.hide()
        })
    } , [dispatch, modal, type, currentPlatform, toast, onCategoryEdit, t])

    const onCategoryRemove= useCallback(async (id: string, type: string) => {
        const confirm = window.confirm('Вы действительно хотите удалить категорию c платформы?');
        if (confirm) {
            TagsAPI.OperationsTagsForContext(
                [
                    {
                        context: currentPlatform?.platform?.context ?? '',
                        appName: currentPlatform?.platform?.appName ?? '',
                        typeOperation: type,tagIds: [id]
                    }
                ]
            ).then((r) => {
                if (r.status === 200) {
                    dispatch(fetchCurrentPlatformData({ platformName: getComputeAppName(currentPlatform?.platform)}))
                    setTime(Date.now());
                }
            })
        }   
    }, [currentPlatform])

    const onLocalLangChange = (arr: any[], lang: string, callback: (test: any[]) => void) => 
        callback(
            arr?.map((i: any) => ({ ...i, currentLang: lang === 'ru' ? 'en' : 'ru' }))
    );
    
    const onChildrenOperation = useCallback((tagOpt: any[], type?: TAG_ADMIN_OPERATION) => {

        if (!type) {
            //@ts-ignore
            TagsAPI.OperationsToCategory(tagOpt)
            onCategoryEdit(currentPlatform?.editCategory?.mainTag?.tagId ?? '')
            setTime(Date.now())
            
            return;
        }

        const data = tagOpt?.map((tag: any) => {
            return {
                tagIds: [tag],
                groupId: currentPlatform?.editCategory?.mainTag?.tagId ?? '',
                typeOperation: TAG_ADMIN_OPERATION[type],
            }
        })
        //@ts-ignore
        TagsAPI.OperationsToCategory([...data])
        onCategoryEdit(currentPlatform?.editCategory?.mainTag?.tagId ?? '')
        setTime(Date.now())

    } , [currentPlatform, onCategoryEdit])

    const addChildrenToCategory = useCallback((tags: Tag<'Local'>[]) => {
        const ids = tags?.map((tag) => tag?.tagId);
        const cTag = currentPlatform?.editCategory?.childrens?.map((tag) => tag?.tagId);

        const deleted = cTag?.filter((value) => !ids?.includes(value))?.map((id) => ({
            tagIds: [id],
            groupId: currentPlatform?.editCategory?.mainTag?.tagId ?? '',
            typeOperation: TAG_ADMIN_OPERATION.remove,
        })) || []
        const added = ids?.filter((value) => !cTag?.includes(value))?.map((id) => ({
            tagIds: [id],
            groupId: currentPlatform?.editCategory?.mainTag?.tagId ?? '',
            typeOperation: TAG_ADMIN_OPERATION.add,
        })) || []
        
        onChildrenOperation([...deleted, ...added])
    } , [onChildrenOperation, currentPlatform])

    const onChildrenRemove = useCallback((id: string) => {
        const confirm = window.confirm('Вы действительно хотите удалить тег из категории?');
        if (confirm) {
            onChildrenOperation([id], TAG_ADMIN_OPERATION.remove);
            setTime(Date.now())
        }
    } , [onChildrenOperation])

    const onChildrenAdd = useCallback((tags: Tag<'Local'>[]) => {
        modal.show('tags', {
            value: tags,
            onChange: (tagsRaw: Tag[]) => {
                addChildrenToCategory(tagsRaw);
                setTime(Date.now());
            },
            onAfterTagCreate: () => {
                onCategoryEdit(currentPlatform?.editCategory?.mainTag?.tagId ?? '')
                setTime(Date.now())
            },
            educational: type === ADMIN_TAGS_TYPE.education,
            saveOnClick: true,
            global: true,
            isTagAdminPage: true
          })
    } , [modal, addChildrenToCategory, onCategoryEdit, currentPlatform])
    
    const onCreateSubCategory = useCallback(async (name: CurrentLangT[]) => {
        //@ts-ignore
        TagsAPI.AddTags([{ 
            name, 
            groupIds: [currentPlatform?.editCategory?.mainTag?.tagId || ''],
            level: TAG_LEVEL.subcategory,
            isEducationTag: false,
        }])
        .then((r) => {
            if (r?.status === 200) {
                dispatch(fetchCurrentPlatformData({ 
                    platformName: getComputeAppName(currentPlatform?.platform),
                }))
                toast.info(`В категории ${currentPlatform?.editCategory.mainTag?.title} создана подкатегория ${Lang(name, i18n.language)}`)
                setTime(Date.now())
                return;
            }

            toast.error(`Что-то пошло не так. Обратитесь к администратору`);
        })
    } , [i18n, currentPlatform, toast])

    const onCreateNewTag = useCallback(async ({ tag, groupId } : {tag: Tag<'Local'>, groupId: string}) => {
        //@ts-ignore
        TagsAPI.OperationsToCategory([ 
            {
                tagIds: [tag?.tagId],
                groupId,
                typeOperation: TAG_ADMIN_OPERATION.add
            }
        ])
        onCategoryEdit(groupId ?? '')
        setTime(Date.now())
    } , [])

    const onCategoryRename = useCallback(async (tag: Tag<'Global'>) => {
        TagsAPI.UpdateTag(tag)
        .then((r) => {
            if (r?.status === 200) {
                dispatch(fetchCurrentPlatformData({ 
                    platformName: getComputeAppName(currentPlatform?.platform),
                    tag
                }))
            }
            if (r?.status === 400) {
                toast.error(`Такое имя категории уже есть`);
                return
            }
        })
    }, [dispatch, currentPlatform])

    const onSwitchCategories = useCallback(async (id: string, isExist?: boolean) => {
        TagsAPI.OperationsTagsForContext([
            {
                context: currentPlatform?.platform?.context ?? '',
                appName: currentPlatform?.platform?.appName ?? '', 
                typeOperation: isExist ? TAG_ADMIN_OPERATION.remove : TAG_ADMIN_OPERATION.add,
                tagIds: [id]
            }
        ])
        .then((r) => {
            if (r?.status === 200) {
                dispatch(fetchCurrentPlatformData({ platformName: getComputeAppName(currentPlatform?.platform)}))
                toast.info(`Категория ${id} ${isExist ? 'удалена' : 'добавлена'} ${isExist ? 'c платформы' : 'на платформу'} ${currentPlatform?.platform?.context}`);
                return;
            }

            toast.error(`Что-то пошло не так. Обратитесь к администратору`);
        })
        setTime(Date.now());
    }, [toast, currentPlatform, dispatch])

    const onCreateCategory = useCallback(async (name: CurrentLangT[]) => {
        TagsAPI.AddCategories([{ name, contextServices }])
        .then((r) => {
            if (r?.status === 200) {
                dispatch(fetchCurrentPlatformData({ platformName: getComputeAppName(currentPlatform?.platform)}))
                toast.info(`Категория ${Lang(name, i18n.language)} добавлена на платформу ${currentPlatform?.platform?.appName}`)
                return;
            }
            if (r?.status === 400) {
                toast.error(`Категория "${Lang(name, i18n.language)}" уже существует`);
                return;
            }
            toast.error(`Что-то пошло не так. Обратитесь к администратору`);
        })
        .catch(err => {
            if (err?.response?.status === 400) {
                toast.error(`Категория "${Lang(name, i18n.language)}" уже существует`);
            } else {
                toast.error(`Что-то пошло не так. Обратитесь к администратору`);
            }
        });
    }, [currentPlatform, toast, i18n, contextServices, dispatch])

    const onUpdatePositions = useCallback(
        async (categories: { id: string, subCategoryIds: string[]}[]) => {
            TagsAPI.SetPositions({
                contextServices,
                categories
            })
            .then((r: any) => {
                if (r?.status === 200) {
                    toast.info(`Порядок категорий обновлён`)
                    return;
                }
                toast.error(`Что-то пошло не так. Обратитесь к администратору`);
            })
        } 
    , [contextServices, toast])


    const handlers = useMemo(() => ({
        onCategoryRemove,
        onChildrenAdd,
        onChildrenRemove,
        onCategoryEdit,
        onCreateSubCategory,
        onCreateNewTag,
        onCategoryRename,
        onLocalLangChange,
        onSwitchCategories,
        onCreateCategory,
        onUpdatePositions,
        onUpdate: (t: any) => setTime(t),
        onTagEdit,
        update: time
    }), [
        time,
        onChildrenRemove,
        onSwitchCategories,
        onCategoryRemove,
        onCreateSubCategory,
        onCreateNewTag,
        onUpdatePositions,
        onCategoryRename,
        onLocalLangChange,
        onCreateCategory,
        onChildrenAdd,
        onCategoryEdit,
        onTagEdit
    ])

    return (
        <Wrapper>
            <Layout 
                title={
                    <Title>{t('main_admin_tag.title')}</Title>
                }
                platformOpt={<Platforms {...handlers} />}
                categoryOpt={<Categories {...handlers} />}
                categoryList={
                    <DroppableCategoryList
                        tags={computeCategories}
                        currentCategory={''}
                        fullWidth
                        {...handlers}
                  />
                }
                newCategory={
                    <CategoryEdit {...handlers} />
                }
            />
        </Wrapper>
    )
}import { FC, useCallback, useMemo, useState } from "react";
import { Title, Wrapper } from "./styles";
import { Layout } from "./components/Layout";
import { getComputeAppName, Platforms } from "./section/Platforms";
import { Categories } from "./section/Categories";
import { API, Lang, Tag, TagsAPI } from "@brg/api";
import { TagAdminActions, TagAdminSelector } from "shared/reducers/tagAdminSlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategoryEdit, fetchCurrentPlatformData, updateEditedCategory } from "shared/actions/tagAdminActions";
import { useTranslation } from "react-i18next";
import DroppableCategoryList from "shared/modals/selectTagsModal/components/DroppableCategory/DroppableCategoryList";
import { CategoryEdit, CurrentLangT } from "./section/CategoryEdit";
import { useModal } from "core";
import { useToasts } from "@brg/ui";
import { LangObjectArray } from "@brg/api/dist/api-core/utils/lang-utils";
import { ADMIN_TAGS_TYPE } from "./types";

export const getLangTitle = (arr: LangObjectArray, lang: string) => {
    return arr?.find((i) => i?.lang.toLowerCase() === lang)?.name
}

export const createDefaultLangWithLocal = () => {
    return [{ name: '', lang: 'ru', currentLang: 'ru'}, { name: '', lang: 'en', currentLang: 'ru'}]
}

export enum TAG_ADMIN_OPERATION {
    remove = 'remove',
    add = 'add'
}

enum TAG_LEVEL {
    common = 0,
    subcategory = 1
}

export const MainTagAdminPage: FC = () => {
    const dispatch = useDispatch();
    const modal = useModal();
    const toast = useToasts();
    const {t, i18n} = useTranslation();
    const [time, setTime] = useState(Date.now());

    const { 
        currentPlatform,
        type
    }  = useSelector(TagAdminSelector);

    const computeCategories = useMemo(
        () => currentPlatform?.categories?.length ? API.toLang(currentPlatform?.categories, i18n.language) : [] 
    , [currentPlatform, i18n.language, time]) as Tag<'Local'>[];

    const contextServices = useMemo(() => [{ 
        service: currentPlatform?.platform?.appName ?? '', 
        context: currentPlatform?.platform?.context ?? '' 
    }], [currentPlatform])

    const onCategoryEdit = useCallback((id: string, isChild?: boolean ) => {
        dispatch(fetchCategoryEdit({ id, isChild }))
        dispatch(TagAdminActions.toggleEditMode(true))
        setTime(Date.now());
    } , [dispatch])
    
    const onTagEdit = useCallback((tagData: any) => {
        modal.show('createTag', {
            isModal: true,
            isEducation: type === ADMIN_TAGS_TYPE.education,
            editTag: tagData,
            onUpdateTag: (updatedTag: any) => {
                dispatch(fetchCurrentPlatformData({ 
                    platformName: getComputeAppName(currentPlatform?.platform),
                }))
                if (currentPlatform?.editCategory?.mainTag?.tagId) {
                    onCategoryEdit(currentPlatform.editCategory.mainTag.tagId);
                }
                toast.info(t('tags_modal.update_tag_success'));
                setTime(Date.now());
            },
            onClose: () => modal.hide()
        })
    } , [dispatch, modal, type, currentPlatform, toast, onCategoryEdit, t])

    const onCategoryRemove= useCallback(async (id: string, type: string) => {
        const confirm = window.confirm('Вы действительно хотите удалить категорию c платформы?');
        if (confirm) {
            TagsAPI.OperationsTagsForContext(
                [
                    {
                        context: currentPlatform?.platform?.context ?? '',
                        appName: currentPlatform?.platform?.appName ?? '',
                        typeOperation: type,tagIds: [id]
                    }
                ]
            ).then((r) => {
                if (r.status === 200) {
                    dispatch(fetchCurrentPlatformData({ platformName: getComputeAppName(currentPlatform?.platform)}))
                    setTime(Date.now());
                }
            })
        }   
    }, [currentPlatform])

    const onLocalLangChange = (arr: any[], lang: string, callback: (test: any[]) => void) => 
        callback(
            arr?.map((i: any) => ({ ...i, currentLang: lang === 'ru' ? 'en' : 'ru' }))
    );
    
    const onChildrenOperation = useCallback((tagOpt: any[], type?: TAG_ADMIN_OPERATION) => {

        if (!type) {
            //@ts-ignore
            TagsAPI.OperationsToCategory(tagOpt)
            onCategoryEdit(currentPlatform?.editCategory?.mainTag?.tagId ?? '')
            setTime(Date.now())
            
            return;
        }

        const data = tagOpt?.map((tag: any) => {
            return {
                tagIds: [tag],
                groupId: currentPlatform?.editCategory?.mainTag?.tagId ?? '',
                typeOperation: TAG_ADMIN_OPERATION[type],
            }
        })
        //@ts-ignore
        TagsAPI.OperationsToCategory([...data])
        onCategoryEdit(currentPlatform?.editCategory?.mainTag?.tagId ?? '')
        setTime(Date.now())

    } , [currentPlatform, onCategoryEdit])

    const addChildrenToCategory = useCallback((tags: Tag<'Local'>[]) => {
        const ids = tags?.map((tag) => tag?.tagId);
        const cTag = currentPlatform?.editCategory?.childrens?.map((tag) => tag?.tagId);

        const deleted = cTag?.filter((value) => !ids?.includes(value))?.map((id) => ({
            tagIds: [id],
            groupId: currentPlatform?.editCategory?.mainTag?.tagId ?? '',
            typeOperation: TAG_ADMIN_OPERATION.remove,
        })) || []
        const added = ids?.filter((value) => !cTag?.includes(value))?.map((id) => ({
            tagIds: [id],
            groupId: currentPlatform?.editCategory?.mainTag?.tagId ?? '',
            typeOperation: TAG_ADMIN_OPERATION.add,
        })) || []
        
        onChildrenOperation([...deleted, ...added])
    } , [onChildrenOperation, currentPlatform])

    const onChildrenRemove = useCallback((id: string) => {
        const confirm = window.confirm('Вы действительно хотите удалить тег из категории?');
        if (confirm) {
            onChildrenOperation([id], TAG_ADMIN_OPERATION.remove);
            setTime(Date.now())
        }
    } , [onChildrenOperation])

    const onChildrenAdd = useCallback((tags: Tag<'Local'>[]) => {
        modal.show('tags', {
            value: tags,
            onChange: (tagsRaw: Tag[]) => {
                addChildrenToCategory(tagsRaw);
                setTime(Date.now());
            },
            onAfterTagCreate: () => {
                onCategoryEdit(currentPlatform?.editCategory?.mainTag?.tagId ?? '')
                setTime(Date.now())
            },
            educational: type === ADMIN_TAGS_TYPE.education,
            saveOnClick: true,
            global: true,
            isTagAdminPage: true
          })
    } , [modal, addChildrenToCategory, onCategoryEdit, currentPlatform])
    
    const onCreateSubCategory = useCallback(async (name: CurrentLangT[]) => {
        //@ts-ignore
        TagsAPI.AddTags([{ 
            name, 
            groupIds: [currentPlatform?.editCategory?.mainTag?.tagId || ''],
            level: TAG_LEVEL.subcategory,
            isEducationTag: false,
        }])
        .then((r) => {
            if (r?.status === 200) {
                dispatch(fetchCurrentPlatformData({ 
                    platformName: getComputeAppName(currentPlatform?.platform),
                }))
                toast.info(`В категории ${currentPlatform?.editCategory.mainTag?.title} создана подкатегория ${Lang(name, i18n.language)}`)
                setTime(Date.now())
                return;
            }

            toast.error(`Что-то пошло не так. Обратитесь к администратору`);
        })
    } , [i18n, currentPlatform, toast])

    const onCreateNewTag = useCallback(async ({ tag, groupId } : {tag: Tag<'Local'>, groupId: string}) => {
        //@ts-ignore
        TagsAPI.OperationsToCategory([ 
            {
                tagIds: [tag?.tagId],
                groupId,
                typeOperation: TAG_ADMIN_OPERATION.add
            }
        ])
        onCategoryEdit(groupId ?? '')
        setTime(Date.now())
    } , [])

    const onCategoryRename = useCallback(async (tag: Tag<'Global'>) => {
        TagsAPI.UpdateTag(tag)
        .then((r) => {
            if (r?.status === 200) {
                dispatch(fetchCurrentPlatformData({ 
                    platformName: getComputeAppName(currentPlatform?.platform),
                    tag
                }))
            }
            if (r?.status === 400) {
                toast.error(`Такое имя категории уже есть`);
                return
            }
        })
    }, [dispatch, currentPlatform])

    const onSwitchCategories = useCallback(async (id: string, isExist?: boolean) => {
        TagsAPI.OperationsTagsForContext([
            {
                context: currentPlatform?.platform?.context ?? '',
                appName: currentPlatform?.platform?.appName ?? '', 
                typeOperation: isExist ? TAG_ADMIN_OPERATION.remove : TAG_ADMIN_OPERATION.add,
                tagIds: [id]
            }
        ])
        .then((r) => {
            if (r?.status === 200) {
                dispatch(fetchCurrentPlatformData({ platformName: getComputeAppName(currentPlatform?.platform)}))
                toast.info(`Категория ${id} ${isExist ? 'удалена' : 'добавлена'} ${isExist ? 'c платформы' : 'на платформу'} ${currentPlatform?.platform?.context}`);
                return;
            }

            toast.error(`Что-то пошло не так. Обратитесь к администратору`);
        })
        setTime(Date.now());
    }, [toast, currentPlatform, dispatch])

    const onCreateCategory = useCallback(async (name: CurrentLangT[]) => {
        TagsAPI.AddCategories([{ name, contextServices }])
        .then((r) => {
            if (r?.status === 200) {
                dispatch(fetchCurrentPlatformData({ platformName: getComputeAppName(currentPlatform?.platform)}))
                toast.info(`Категория ${Lang(name, i18n.language)} добавлена на платформу ${currentPlatform?.platform?.appName}`)
                return;
            }
            if (r?.status === 400) {
                toast.error(`Категория "${Lang(name, i18n.language)}" уже существует`);
                return;
            }
            toast.error(`Что-то пошло не так. Обратитесь к администратору`);
        })
        .catch(err => {
            if (err?.response?.status === 400) {
                toast.error(`Категория "${Lang(name, i18n.language)}" уже существует`);
            } else {
                toast.error(`Что-то пошло не так. Обратитесь к администратору`);
            }
        });
    }, [currentPlatform, toast, i18n, contextServices, dispatch])

    const onUpdatePositions = useCallback(
        async (categories: { id: string, subCategoryIds: string[]}[]) => {
            TagsAPI.SetPositions({
                contextServices,
                categories
            })
            .then((r: any) => {
                if (r?.status === 200) {
                    toast.info(`Порядок категорий обновлён`)
                    return;
                }
                toast.error(`Что-то пошло не так. Обратитесь к администратору`);
            })
        } 
    , [contextServices, toast])


    const handlers = useMemo(() => ({
        onCategoryRemove,
        onChildrenAdd,
        onChildrenRemove,
        onCategoryEdit,
        onCreateSubCategory,
        onCreateNewTag,
        onCategoryRename,
        onLocalLangChange,
        onSwitchCategories,
        onCreateCategory,
        onUpdatePositions,
        onUpdate: (t: any) => setTime(t),
        onTagEdit,
        update: time
    }), [
        time,
        onChildrenRemove,
        onSwitchCategories,
        onCategoryRemove,
        onCreateSubCategory,
        onCreateNewTag,
        onUpdatePositions,
        onCategoryRename,
        onLocalLangChange,
        onCreateCategory,
        onChildrenAdd,
        onCategoryEdit,
        onTagEdit
    ])

    return (
        <Wrapper>
            <Layout 
                title={
                    <Title>{t('main_admin_tag.title')}</Title>
                }
                platformOpt={<Platforms {...handlers} />}
                categoryOpt={<Categories {...handlers} />}
                categoryList={
                    <DroppableCategoryList
                        tags={computeCategories}
                        currentCategory={''}
                        fullWidth
                        {...handlers}
                  />
                }
                newCategory={
                    <CategoryEdit {...handlers} />
                }
            />
        </Wrapper>
    )
}